import '../styles/globals.css'
import React, { useEffect, useMemo, useState } from 'react'
import Script from 'next/script'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import { ParallaxProvider } from 'react-scroll-parallax'
import { DefaultSeo, OrganizationJsonLd } from 'next-seo'
import Layout from '../components/shared/Layout/Layout'
import i18nextConfig from '../../next-i18next.config'
import {
  ModalConsumer,
  ModalContextForHooksProvider,
} from '../providers/ModalProvider'
import { NotificationProvider } from '../providers/NotificationProvider'
import useLocale from '../hooks/useLocale'
import { getCanonicalPath, getFullPath } from '../lib/utils/language'
import CookieConsent from '../components/shared/CookieConsent'
import {
  getStoredConsent,
  storeConsent,
  type ConsentChoice,
} from '../lib/cookieConsent'
import AnalyticsEvents from '../components/shared/AnalyticsEvents'
import gtmService from '../services/gtm.service'

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const { locale: currentLocale } = useLocale()

  const path = useMemo(() => router.asPath, [router.asPath])
  const canonicalUrl = useMemo(
    () => getCanonicalPath(getFullPath(path, currentLocale)),
    [path, currentLocale],
  )

  // Cookie consent gates third-party scripts (GTM). Until the visitor accepts,
  // no analytics/marketing script is loaded.
  const [cookieConsent, setCookieConsent] = useState<ConsentChoice | null>(null)
  const [consentChecked, setConsentChecked] = useState(false)

  useEffect(() => {
    const storedConsent = getStoredConsent()
    setCookieConsent(storedConsent)
    if (storedConsent) {
      gtmService.consentUpdate(storedConsent)
    }
    setConsentChecked(true)
  }, [])

  const handleConsent = (choice: ConsentChoice) => {
    storeConsent(choice)
    gtmService.consentUpdate(choice)
    setCookieConsent(choice)
  }

  return (
    <NotificationProvider>
      <ModalContextForHooksProvider>
        <ModalConsumer />
        <Script
          id="gtm-consent-default"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer=window.dataLayer||[];
function gtag(){dataLayer.push(arguments);}
gtag('consent','default',{
  analytics_storage:'denied',
  ad_storage:'denied',
  ad_user_data:'denied',
  ad_personalization:'denied',
  wait_for_update:500
});`,
          }}
        />
        {GTM_ID && cookieConsent === 'granted' && (
          <Script
            id="google-tag-manager"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer', '${GTM_ID}');`,
            }}
          ></Script>
        )}
        {GTM_ID && cookieConsent === 'granted' && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
  height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            }}
          ></noscript>
        )}
        {consentChecked && cookieConsent === null && (
          <CookieConsent onDecision={handleConsent} />
        )}
        <ParallaxProvider>
          <Layout showPageLoader={false}>
            <AnalyticsEvents />
            <DefaultSeo
              canonical={canonicalUrl}
              openGraph={{
                type: 'website',
                locale: currentLocale,
                url: canonicalUrl,
                siteName: 'Shelters',
                images: [
                  {
                    url: `${process.env.NEXT_PUBLIC_BASEURL}/img/logos/shelters/logo-full-x1700.png`,
                    width: 1700,
                    height: 899,
                    alt: 'Shelters logo',
                  },
                ],
              }}
              languageAlternates={i18nextConfig.i18n.locales
                .map((locale: string) => ({
                  hrefLang: locale,
                  href: `${getFullPath(router.asPath, locale)}`,
                }))
                .concat({
                  hrefLang: 'x-default',
                  href: `${getFullPath(
                    router.asPath,
                    process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
                  )}`,
                })}
              twitter={{
                site: '@' + process.env.NEXT_PUBLIC_TWITTER_ACCOUNT,
                cardType: 'summary_large_image',
              }}
              facebook={{
                appId: process.env.NEXT_PUBLIC_FACEBOOK_PAGE_ID,
              }}
              themeColor="transparent"
              titleTemplate="%s | Shelters"
              additionalMetaTags={[
                {
                  name: 'viewport',
                  content:
                    'width=device-width, initial-scale=1, viewport-fit=cover',
                },
                {
                  name: 'apple-mobile-web-app-capable',
                  content: 'yes',
                },
                {
                  name: 'apple-mobile-web-app-status-bar-style',
                  content: 'black-translucent',
                },
              ]}
              additionalLinkTags={[
                {
                  rel: 'icon',
                  href: '/img/logos/shelters/logo-32x32-min.png',
                  sizes: '32x32',
                },
                {
                  rel: 'icon',
                  href: '/img/logos/shelters/logo-192x192-min.png',
                  sizes: '192x192',
                },
                {
                  rel: 'apple-touch-icon',
                  href: '/img/logos/shelters/logo-180x180-min.png',
                  sizes: '180x180',
                },
                {
                  rel: 'manifest',
                  href: `/manifest-${currentLocale}.json`,
                },
              ]}
            />
            <OrganizationJsonLd
              name={'Shelters'}
              url={process.env.NEXT_PUBLIC_BASEURL}
              logo={
                process.env.NEXT_PUBLIC_BASEURL +
                '/img/logos/shelters/logo-full-x500.png'
              }
              sameAs={[
                process.env.NEXT_PUBLIC_FACEBOOK_PAGE,
                process.env.NEXT_PUBLIC_TWITTER_PAGE,
                process.env.NEXT_PUBLIC_INSTAGRAM_PAGE,
                process.env.NEXT_PUBLIC_LINKEDIN_PAGE,
              ]}
            />
            <Component {...pageProps} />
          </Layout>
        </ParallaxProvider>
      </ModalContextForHooksProvider>
    </NotificationProvider>
  )
}

export default appWithTranslation(App, i18nextConfig)
